/**
 * SMS Campaign Stats Page
 * Route: /marketing/sms-campaigns/:id/stats
 *
 * Mirrors the EmailCampaignStatsPage pattern: stat summary cards,
 * campaign details grid, phone mockup preview, contacts link,
 * SQS worker status, and resume/retry actions for stalled campaigns.
 */
import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Copy, MoreVertical, Play, RotateCcw } from 'lucide-react';
import { SmsPhoneMockup } from '@/components/sms-editor';
import { MarketingHistoryCard } from '@/components/marketing/MarketingHistoryCard';
import { statBox, contactsPath } from '@/components/marketing/statBox';
import { apiClient } from '@/services/apiClient';
import * as marketingSvc from '@/services/marketing.service';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { cn, formatPhone } from '@/lib/utils';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { pacificToWallClock, getTimezoneAbbreviation } from '@/lib/facilityTimezone';

// Campaign timestamps arrive as Pacific wall-clock DATETIME strings
// (config('app.timezone')); reproject into the viewer's zone and suffix the
// abbreviation so the send time is unambiguous.
function formatSentDate(dateStr: string | null | undefined, tz: string): string {
  if (!dateStr) return '\u2014';
  const m = dateStr.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  if (!m) return '\u2014';
  const wall = pacificToWallClock(m[1], m[2], tz);
  const [y, mo, d] = wall.date.split('-').map(Number);
  const [h, mi] = wall.time.split(':').map(Number);
  const dateObj = new Date(y, mo - 1, d, h, mi);
  if (isNaN(dateObj.getTime())) return '\u2014';
  const datePart = dateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  const timePart = dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${datePart} ${timePart} ${getTimezoneAbbreviation(tz, dateObj)}`;
}

function fmtNum(n: number | null | undefined): string {
  if (n == null) return '0';
  return n.toLocaleString();
}

const STAT_CARDS: {
  key: string;
  label: string;
  color: string;
  warnWhenPositive?: boolean;
  /** The sent-contacts list's filters for the people behind the number. */
  link: Record<string, string>;
}[] = [
  // 'sent_any' because SMS Sent counts sent and delivered — a delivered text was also sent.
  { key: 'totalSent', label: 'SMS Sent', color: '#00aff0', link: { filter: 'sent_any' } },
  { key: 'totalDelivered', label: 'Delivered', color: '#28a745', link: { filter: 'delivered' } },
  { key: 'totalFailed', label: 'Failed', color: '#dc3545', warnWhenPositive: true, link: { filter: 'failed' } },
  { key: 'totalUndelivered', label: 'Undelivered', color: '#ffc107', warnWhenPositive: true, link: { filter: 'undelivered' } },
  { key: 'totalUnsubscribed', label: 'Unsubscribed', color: '#dc3545', warnWhenPositive: true, link: { filter: 'unsubscribed' } },
  { key: 'totalQueued', label: 'Queued', color: '#f97316', warnWhenPositive: true, link: { filter: 'queued' } },
  { key: 'totalSending', label: 'Sending', color: '#8b5cf6', warnWhenPositive: true, link: { filter: 'sending' } },
  // Request-info leads (top-level `leads`, not `stats`): the campaign's own
  // recipients and whoever they forwarded the link to, together; opens
  // "Created a lead" (either kind).
  { key: 'leads', label: 'Leads', color: '#fd7e14', link: { lead: '1' } },
];

export default function SmsCampaignStatsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const campaignId = Number(id);
  const toast = useToast();
  const qc = useQueryClient();
  const { user } = useAuth();
  const canManageSend = (user?.userLevel ?? 0) >= 5;
  const userTz = useUserTimezone();

  const [showPreview, setShowPreview] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneName, setCloneName] = useState('');
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  const [confirmResume, setConfirmResume] = useState(false);
  const [confirmRetry, setConfirmRetry] = useState(false);
  const overflowRef = useRef<HTMLDivElement>(null);

  const cloneMut = useMutation({
    mutationFn: (name: string) =>
      apiClient.post(`/marketing/sms-campaigns/${campaignId}/clone`, { name }).then((r) => r.data.data),
    onSuccess: (data: any) => {
      toast.success('Campaign cloned');
      setShowCloneModal(false);
      qc.invalidateQueries({ queryKey: ['marketing', 'sms-campaigns'] });
      navigate(`/marketing/sms-campaigns/${data.smsCampaignId ?? data.smsCampaignID}`);
    },
    onError: () => toast.error('Failed to clone'),
  });

  const resumeMut = useMutation({
    mutationFn: () => marketingSvc.resumeSmsCampaign(campaignId),
    onSuccess: (res) => {
      toast.success(res.message);
      setConfirmResume(false);
      qc.invalidateQueries({ queryKey: ['sms-campaign-stats-page', campaignId] });
      qc.invalidateQueries({ queryKey: ['marketing-history'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Failed to resume campaign');
      setConfirmResume(false);
    },
  });

  const retryMut = useMutation({
    mutationFn: () => marketingSvc.retryFailedSmsCampaign(campaignId),
    onSuccess: (res) => {
      toast.success(res.message);
      setConfirmRetry(false);
      qc.invalidateQueries({ queryKey: ['sms-campaign-stats-page', campaignId] });
      qc.invalidateQueries({ queryKey: ['marketing-history'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Failed to retry');
      setConfirmRetry(false);
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ['sms-campaign-stats-page', campaignId],
    queryFn: () => marketingSvc.getSmsCampaignStats(campaignId),
    enabled: !!campaignId,
    refetchInterval: (query) => {
      const d = query.state.data;
      if (d?.campaign?.status === 'sending') return 30000;
      return false;
    },
  });

  const campaign = data?.campaign;
  const stats = data?.stats;
  const sqsInfo = data?.sqs;
  const lists = data?.lists ?? [];
  const contactsCount = data?.contactsCount ?? 0;
  const isActivelySending = sqsInfo?.worker_active === true;
  const hasQueued = (stats?.totalQueued ?? 0) > 0 || (stats?.totalSending ?? 0) > 0;
  const hasRetryableFailed = (stats?.totalRetryableFailed ?? 0) > 0;

  useEffect(() => {
    if (!showOverflowMenu) return;
    const handler = (e: MouseEvent) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target as Node)) {
        setShowOverflowMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showOverflowMenu]);

  useEffect(() => {
    document.body.style.overflow = showPreview ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showPreview]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!campaign || !stats) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 20px', color: '#999' }}>
        <p style={{ fontSize: 18 }}>Campaign not found.</p>
        <button
          type="button"
          onClick={() => navigate('/marketing/sms-campaigns')}
          style={{ marginTop: 16, color: '#00aff0', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}
        >
          Back to Campaigns
        </button>
      </div>
    );
  }

  const statusBadgeClass =
    campaign.status === 'sent'
      ? 'ssp-badge-sent'
      : campaign.status === 'scheduled'
        ? 'ssp-badge-scheduled'
        : campaign.status === 'sending'
          ? 'ssp-badge-sending'
          : 'ssp-badge-default';

  // Missing `leads` (an API that predates request-info links) reads as zeros.
  const leads = marketingSvc.leadCountsOf(data);
  const cardValues: Record<string, number> = {
    ...(stats as unknown as Record<string, number>),
    leads: leads.recipient + leads.referral,
  };

  return (
    <>
      <style>{`
        .ssp-page { width: 100%; padding: 20px; margin: 0 auto; }

        .ssp-header {
          display: flex; justify-content: space-between; align-items: flex-start;
          margin-bottom: 20px; padding-bottom: 15px; border-bottom: 1px solid #e5e5e5;
          /* Pinned to the very top: the negative margin swallows the page's 20px
             top padding so the header starts flush at scroll 0 and never travels;
             opaque so content slides underneath. */
          position: sticky; top: 0; z-index: 100; background: #fff;
          margin-top: -20px; padding-top: 20px;
        }
        .ssp-header h1 { margin: 0 0 8px 0; font-size: 24px; font-weight: 600; color: #333; }
        .ssp-meta { display: flex; align-items: center; gap: 15px; font-size: 14px; color: #666; }

        .ssp-badge {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 4px 10px; border-radius: 12px; font-size: 12px;
          font-weight: 600; text-transform: capitalize;
        }
        .ssp-badge::before {
          content: ''; width: 8px; height: 8px; border-radius: 50%; background: currentColor;
        }
        .ssp-badge-sent { background: #d4edda; color: #155724; }
        .ssp-badge-scheduled { background: #fff3cd; color: #856404; }
        .ssp-badge-sending { background: #cce5ff; color: #004085; }
        .ssp-badge-default { background: #e9ecef; color: #666; }

        .ssp-header-right {
          display: flex; flex-direction: column; align-items: flex-end; gap: 12px;
        }

        .ssp-summary {
          display: grid; grid-template-columns: repeat(8, 1fr); gap: 0;
          background: white; border: 1px solid #e5e5e5; border-radius: 8px;
          margin-bottom: 25px; overflow: hidden;
        }
        .ssp-stat-card {
          padding: 20px 15px; text-align: center; border-right: 1px solid #e5e5e5;
        }
        .ssp-stat-card:last-child { border-right: none; }
        .ssp-stat-value { font-size: 28px; font-weight: 600; margin-bottom: 6px; }
        .ssp-stat-value.zero { color: #999; }
        .ssp-stat-label {
          font-size: 11px; font-weight: 600; text-transform: uppercase;
          color: #666; letter-spacing: 0.5px;
        }

        .ssp-section {
          background: white; border: 1px solid #e5e5e5; border-radius: 8px;
          margin-bottom: 25px; overflow: hidden;
        }
        .ssp-section-header {
          display: flex; align-items: center; gap: 8px;
          padding: 15px 20px; border-bottom: 1px solid #e5e5e5;
          font-weight: 600; color: #333;
        }

        .ssp-details-grid { display: grid; grid-template-columns: 200px 1fr; gap: 0; }
        .ssp-detail-label {
          padding: 15px 20px; font-weight: 600; color: #333;
          background: #f8f9fa; border-bottom: 1px solid #e5e5e5; font-size: 14px;
        }
        .ssp-detail-value {
          padding: 15px 20px; color: #666; border-bottom: 1px solid #e5e5e5; font-size: 14px;
        }
        .ssp-detail-value a { color: #00aff0; text-decoration: none; }
        .ssp-detail-value a:hover { text-decoration: underline; }

        .ssp-list-tags { display: flex; flex-wrap: wrap; gap: 8px; }
        .ssp-list-tag {
          display: inline-block; padding: 4px 10px; background: #e7f3ff;
          color: #00aff0; border-radius: 4px; font-size: 13px;
        }

        .ssp-contacts-link {
          display: inline-flex; align-items: center; gap: 8px; text-decoration: none;
        }
        .ssp-contacts-link:hover { opacity: 0.85; }

        .ssp-view-content-btn {
          padding: 6px 14px; font-size: 13px; font-weight: 600;
          color: #333; background: white; border: 1px solid #ddd;
          border-radius: 5px; cursor: pointer; transition: background 0.2s;
          display: inline-flex; align-items: center; gap: 5px;
        }
        .ssp-view-content-btn:hover { background: #f5f5f5; }

        .ssp-overflow-wrap { position: relative; }
        .ssp-overflow-btn {
          display: inline-flex; align-items: center; justify-content: center;
          width: 32px; height: 32px; border: 1px solid #ddd; border-radius: 5px;
          background: white; cursor: pointer; color: #666; transition: background 0.2s;
        }
        .ssp-overflow-btn:hover { background: #f5f5f5; }
        .ssp-overflow-menu {
          position: absolute; right: 0; top: 100%; margin-top: 4px; z-index: 50;
          min-width: 170px; background: white; border: 1px solid #e5e5e5;
          border-radius: 6px; box-shadow: 0 4px 16px rgba(0,0,0,0.12);
          padding: 4px 0;
        }
        .ssp-overflow-item {
          display: flex; align-items: center; gap: 8px; width: 100%;
          padding: 8px 14px; border: none; background: none;
          font-size: 13px; color: #333; cursor: pointer; text-align: left;
        }
        .ssp-overflow-item:hover { background: #f5f5f5; }

        .ssp-modal-overlay {
          display: flex; position: fixed; inset: 0; background: rgba(0,0,0,0.5);
          z-index: 9999; justify-content: center; align-items: center;
        }
        .ssp-modal {
          background: #fff; border-radius: 10px; width: 90%; max-width: 400px;
          max-height: 85vh; display: flex; flex-direction: column;
          box-shadow: 0 8px 30px rgba(0,0,0,0.25);
        }
        .ssp-modal-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 16px 20px; border-bottom: 1px solid #e5e5e5;
        }
        .ssp-modal-header h3 { margin: 0; font-size: 16px; font-weight: 600; }
        .ssp-modal-close {
          background: none; border: none; font-size: 22px; cursor: pointer;
          color: #999; padding: 0 4px; line-height: 1;
        }
        .ssp-modal-close:hover { color: #333; }
        .ssp-modal-body { flex: 1; overflow: auto; display: flex; justify-content: center; padding: 20px; }

        .ssp-clone-modal {
          background: #fff; border-radius: 10px; width: 90%; max-width: 450px;
          box-shadow: 0 8px 30px rgba(0,0,0,0.25);
        }
        .ssp-clone-body { padding: 20px; }
        .ssp-clone-input {
          width: 100%; padding: 8px 12px; border: 1px solid #ddd; border-radius: 6px;
          font-size: 14px; margin-bottom: 16px;
        }
        .ssp-clone-input:focus { outline: none; border-color: #00aff0; }
        .ssp-clone-actions { display: flex; justify-content: flex-end; gap: 8px; }
        .ssp-clone-cancel {
          padding: 8px 16px; border: 1px solid #ddd; border-radius: 6px;
          background: white; cursor: pointer; font-size: 13px;
        }
        .ssp-clone-submit {
          padding: 8px 16px; border: none; border-radius: 6px;
          background: #00aff0; color: white; cursor: pointer; font-size: 13px; font-weight: 600;
        }
        .ssp-clone-submit:disabled { opacity: 0.5; cursor: not-allowed; }

        .ssp-actions {
          display: flex; align-items: center; gap: 16px; flex-wrap: wrap;
          background: white; border: 1px solid #e5e5e5; border-radius: 8px;
          margin-bottom: 25px; padding: 16px 20px;
        }
        .ssp-actions-title {
          font-weight: 600; font-size: 14px; color: #333; margin-right: 8px;
        }
        .ssp-action-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 16px; border-radius: 6px; font-size: 13px;
          font-weight: 600; cursor: pointer; transition: opacity 0.2s;
          border: none;
        }
        .ssp-action-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .ssp-action-btn:hover:not(:disabled) { opacity: 0.85; }
        .ssp-action-btn--resume { background: #059669; color: white; }
        .ssp-action-btn--retry { background: #dc3545; color: white; }
        .ssp-sqs-indicator {
          display: inline-flex; align-items: center; gap: 8px;
          margin-left: auto; font-size: 12px; color: #666;
        }
        .ssp-sqs-dot {
          width: 8px; height: 8px; border-radius: 50%;
        }
        .ssp-sqs-dot--active { background: #10b981; }
        .ssp-sqs-dot--stalled { background: #ef4444; animation: pulse 1.4s infinite; }

        .ssp-confirm-overlay {
          display: flex; position: fixed; inset: 0; background: rgba(0,0,0,0.5);
          z-index: 9999; justify-content: center; align-items: center;
        }
        .ssp-confirm-modal {
          background: white; border-radius: 10px; padding: 24px;
          max-width: 420px; width: 90%; box-shadow: 0 8px 30px rgba(0,0,0,0.25);
        }
        .ssp-confirm-modal h3 { margin: 0 0 12px; font-size: 16px; font-weight: 600; }
        .ssp-confirm-modal p { margin: 0 0 20px; font-size: 14px; color: #666; line-height: 1.5; }
        .ssp-confirm-actions { display: flex; justify-content: flex-end; gap: 8px; }

        @media (max-width: 768px) {
          .ssp-summary { grid-template-columns: repeat(2, 1fr); }
          .ssp-stat-card:nth-child(2n) { border-right: none; }
          .ssp-stat-card:nth-child(n+3) { border-top: 1px solid #e5e5e5; }
          .ssp-details-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="ssp-page">
        {/* Header */}
        <div className="ssp-header">
          <div>
            <h1>{campaign.name}</h1>
            <div className="ssp-meta">
              <span className={`ssp-badge ${statusBadgeClass}`}>
                {campaign.status?.charAt(0).toUpperCase() + campaign.status?.slice(1)}
              </span>
              {campaign.sentAt && <span>Sent on {formatSentDate(campaign.sentAt, userTz)}</span>}
            </div>
            {campaign.description && (
              <div style={{ marginTop: 8, fontSize: 13, color: '#888', lineHeight: 1.5 }}>
                {campaign.description}
              </div>
            )}
          </div>

          <div className="ssp-header-right">
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Link to={`/marketing/sms-campaigns/${id}/sent-contacts`} className="ssp-contacts-link">
                <span style={{ fontSize: 20, fontWeight: 700, color: '#00aff0' }}>
                  {fmtNum(contactsCount)}
                </span>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>
                  {contactsCount === 1 ? 'contact' : 'contacts'}
                </span>
              </Link>
              <button
                type="button"
                className="ssp-view-content-btn"
                onClick={() => setShowPreview(true)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                View Content
              </button>
              <div className="ssp-overflow-wrap" ref={overflowRef}>
                <button
                  type="button"
                  className="ssp-overflow-btn"
                  onClick={() => setShowOverflowMenu((v) => !v)}
                >
                  <MoreVertical style={{ width: 16, height: 16 }} />
                </button>
                {showOverflowMenu && (
                  <div className="ssp-overflow-menu">
                    <button
                      type="button"
                      className="ssp-overflow-item"
                      onClick={() => {
                        setShowOverflowMenu(false);
                        setCloneName(campaign.name + ' (Copy)');
                        setShowCloneModal(true);
                      }}
                    >
                      <Copy style={{ width: 14, height: 14 }} />
                      Clone Campaign
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="ssp-summary">
          {STAT_CARDS.map((card) => {
            const val = cardValues[card.key] ?? 0;
            const isZero = val === 0;
            const color = isZero ? undefined : card.color;
            // Every box opens the sent-contacts list filtered to the people behind it.
            const box = statBox(isZero ? null : () => navigate(contactsPath(`/marketing/sms-campaigns/${id}/sent-contacts`, card.link)));
            return (
              <div key={card.key} className={cn('ssp-stat-card', box.className)} {...box.props}>
                <div
                  className={`ssp-stat-value${isZero ? ' zero' : ''}`}
                  style={color ? { color } : undefined}
                >
                  {fmtNum(val)}
                </div>
                <div className="ssp-stat-label">{card.label}</div>
              </div>
            );
          })}
        </div>

        {/* Campaign Actions — level 5 only (resume/retry actions + SQS worker status) */}
        {canManageSend && (hasQueued || hasRetryableFailed || sqsInfo) && (
          <div className="ssp-actions">
            <span className="ssp-actions-title">Campaign Actions</span>
            {canManageSend && hasQueued && !isActivelySending && (
              <button
                type="button"
                className="ssp-action-btn ssp-action-btn--resume"
                disabled={resumeMut.isPending}
                onClick={() => setConfirmResume(true)}
              >
                <Play style={{ width: 14, height: 14 }} />
                {resumeMut.isPending ? 'Resuming...' : 'Resume Sending'}
              </button>
            )}
            {canManageSend && hasRetryableFailed && !isActivelySending && (
              <button
                type="button"
                className="ssp-action-btn ssp-action-btn--retry"
                disabled={retryMut.isPending}
                onClick={() => setConfirmRetry(true)}
              >
                <RotateCcw style={{ width: 14, height: 14 }} />
                {retryMut.isPending ? `Retrying ${fmtNum(stats?.totalRetryableFailed ?? 0)}...` : `Retry Failed (${fmtNum(stats?.totalRetryableFailed ?? 0)})`}
              </button>
            )}
            {canManageSend && sqsInfo && (
              <div className="ssp-sqs-indicator">
                <span className={`ssp-sqs-dot ${sqsInfo.worker_active ? 'ssp-sqs-dot--active' : 'ssp-sqs-dot--stalled'}`} />
                {sqsInfo.worker_active
                  ? `Worker Active (${sqsInfo.pending ?? 0} pending, ${sqsInfo.in_flight ?? 0} in flight)`
                  : `Worker Stalled \u2014 ${sqsInfo.pending ?? 0} pending, ${sqsInfo.in_flight ?? 0} in flight`
                }
              </div>
            )}
          </div>
        )}

        {/* Details */}
        <div className="ssp-section">
          <div className="ssp-section-header">Details</div>
          <div className="ssp-details-grid">
            <div style={{ display: 'contents' }}>
              <div className="ssp-detail-label">Campaign Name</div>
              <div className="ssp-detail-value">{campaign.name}</div>
            </div>

            {campaign.description && (
              <div style={{ display: 'contents' }}>
                <div className="ssp-detail-label">Description</div>
                <div className="ssp-detail-value" style={{ color: '#888' }}>{campaign.description}</div>
              </div>
            )}

            <div style={{ display: 'contents' }}>
              <div className="ssp-detail-label">From Number</div>
              <div className="ssp-detail-value">{campaign.fromNumber ? formatPhone(campaign.fromNumber) : '\u2014'}</div>
            </div>

            {campaign.sentAt && (
              <div style={{ display: 'contents' }}>
                <div className="ssp-detail-label">Sent At</div>
                <div className="ssp-detail-value">{formatSentDate(campaign.sentAt, userTz)}</div>
              </div>
            )}

            {lists.length > 0 && (
              <div style={{ display: 'contents' }}>
                <div className="ssp-detail-label">Lists</div>
                <div className="ssp-detail-value">
                  <div className="ssp-list-tags">
                    {lists.map((l) => (
                      <span key={l.id} className="ssp-list-tag" title={l.description ?? ''}>
                        {l.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: 'contents' }}>
              <div className="ssp-detail-label">Recipients</div>
              <div className="ssp-detail-value">
                <Link
                  to={`/marketing/sms-campaigns/${id}/sent-contacts`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                >
                  <span style={{ fontSize: 18, fontWeight: 600, color: '#00aff0' }}>
                    {fmtNum(contactsCount)}
                  </span>
                  <span>{contactsCount === 1 ? 'contact' : 'contacts'}</span>
                </Link>
              </div>
            </div>

            <div style={{ display: 'contents' }}>
              <div className="ssp-detail-label">Message</div>
              <div className="ssp-detail-value" style={{ whiteSpace: 'pre-wrap' }}>
                {campaign.messageBody || '\u2014'}
              </div>
            </div>
          </div>
        </div>

        {/* History */}
        <MarketingHistoryCard type="sms_campaign" id={campaignId} className="mb-[25px]" />
      </div>

      {/* SMS Preview Modal */}
      {showPreview && (
        <div
          className="ssp-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setShowPreview(false); }}
        >
          <div className="ssp-modal">
            <div className="ssp-modal-header">
              <h3>SMS Preview</h3>
              <button className="ssp-modal-close" onClick={() => setShowPreview(false)}>&times;</button>
            </div>
            <div className="ssp-modal-body">
              {/* Shared with the SMS editor's preview pane and the automation
                  step modal, so all three phone views stay identical. */}
              <SmsPhoneMockup
                messageBody={campaign.messageBody || ''}
                mediaUrls={campaign.mediaUrls ?? []}
                headerText="SMS Preview"
              />
            </div>
          </div>
        </div>
      )}

      {/* Clone Modal */}
      {showCloneModal && (
        <div
          className="ssp-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setShowCloneModal(false); }}
        >
          <div className="ssp-clone-modal">
            <div className="ssp-modal-header">
              <h3>Clone Campaign</h3>
              <button className="ssp-modal-close" onClick={() => setShowCloneModal(false)}>&times;</button>
            </div>
            <div className="ssp-clone-body">
              <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600, color: '#333' }}>
                New Campaign Name
              </label>
              <input
                className="ssp-clone-input"
                value={cloneName}
                onChange={(e) => setCloneName(e.target.value)}
                autoFocus
              />
              <div className="ssp-clone-actions">
                <button className="ssp-clone-cancel" onClick={() => setShowCloneModal(false)}>Cancel</button>
                <button
                  className="ssp-clone-submit"
                  disabled={!cloneName.trim() || cloneMut.isPending}
                  onClick={() => cloneMut.mutate(cloneName.trim())}
                >
                  {cloneMut.isPending ? 'Cloning...' : 'Clone'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resume Confirmation */}
      {confirmResume && (
        <div className="ssp-confirm-overlay" onClick={(e) => { if (e.target === e.currentTarget) setConfirmResume(false); }}>
          <div className="ssp-confirm-modal">
            <h3>Resume Campaign</h3>
            <p>
              This will re-dispatch {fmtNum(stats?.totalQueued ?? 0)} queued contacts
              {(stats?.totalSending ?? 0) > 0 && ` and reset ${fmtNum(stats?.totalSending ?? 0)} stuck contacts`} for delivery.
              Any contacts already sent will not be duplicated.
            </p>
            <div className="ssp-confirm-actions">
              <button className="ssp-clone-cancel" onClick={() => setConfirmResume(false)}>Cancel</button>
              <button
                className="ssp-action-btn ssp-action-btn--resume"
                disabled={resumeMut.isPending}
                onClick={() => resumeMut.mutate()}
              >
                {resumeMut.isPending ? 'Resuming...' : 'Confirm Resume'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Retry Failed Confirmation */}
      {confirmRetry && (
        <div className="ssp-confirm-overlay" onClick={(e) => { if (e.target === e.currentTarget) setConfirmRetry(false); }}>
          <div className="ssp-confirm-modal">
            <h3>Retry Failed Contacts</h3>
            <p>
              This will reset {fmtNum(stats?.totalRetryableFailed ?? 0)} contacts that failed due to
              system errors (rate limits, insufficient funds, timeouts) back to queued and re-dispatch
              them for delivery. Contacts with permanent errors (invalid numbers) will not be retried.
            </p>
            <div className="ssp-confirm-actions">
              <button className="ssp-clone-cancel" onClick={() => setConfirmRetry(false)}>Cancel</button>
              <button
                className="ssp-action-btn ssp-action-btn--retry"
                disabled={retryMut.isPending}
                onClick={() => retryMut.mutate()}
              >
                {retryMut.isPending ? 'Retrying...' : 'Confirm Retry'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
